import { CommonModule } from '@angular/common';
import { Component, effect, inject, OnInit, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import {
  WhatsappConfigDataService,
  WhatsappConfigService,
} from '@nomoh-monorepo/data-access-bardo-administrador';
import { NotificacaoService } from '@nomoh-monorepo/data-access-shared-notificacao';
import { HandleEvent, ISession } from '@nomoh-monorepo/shared-types';
import { AccordionModule } from 'primeng/accordion';

import { ButtonModule } from 'primeng/button';
import { RadioButtonModule } from 'primeng/radiobutton';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TooltipModule } from 'primeng/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'lib-whatsapp-settings-panel',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    AccordionModule,
    ButtonModule,
    RadioButtonModule,
    ToggleSwitchModule,
    TooltipModule,
    TranslateModule,
  ],
  templateUrl: './whatsapp-settings-panel.component.html',
  styleUrls: ['./whatsapp-settings-panel.component.scss'],
})
export class WhatsappSettingsPanelComponent implements OnInit {
  private whatsappService = inject(WhatsappConfigService);
  private whatsappConfigDataService = inject(WhatsappConfigDataService);

  private notificacao = inject(NotificacaoService);
  private fb = inject(FormBuilder);
  private translate = inject(TranslateService);

  settingsForm!: FormGroup;
  private _salvando = signal(false);
  $salvando = this._salvando.asReadonly();

  constructor() {
    effect(() => {
      if (this.whatsappConfigDataService.$whatsappConfig().settings) {
        this.inicializarForm();
      }
    });
  }
  ngOnInit(): void {
    this.inicializarForm();
  }

  private inicializarForm(): void {
    const currentSettings =
      this.whatsappConfigDataService.$whatsappConfig()?.settings;

    this.settingsForm = this.fb.group({
      rejectCalls: [currentSettings?.rejectCalls ?? true],
      alwaysOnline: [currentSettings?.alwaysOnline ?? true],
      ignoreGroups: [currentSettings?.ignoreGroups ?? true],
      ignoreBroadcasts: [currentSettings?.ignoreBroadcasts ?? true],
      automaticSchedule: [currentSettings?.automaticSchedule ?? true],
      automaticScheduleConfirmation: [
        currentSettings?.automaticScheduleConfirmation ?? true,
      ],
    });
  }

  async salvar(): Promise<void> {
    if (this.settingsForm.invalid) {
      this.notificacao.notificacaoWarn(
        this.translate.instant('BARDO_WHATSAPP.CONFIGURACOES.FORM_INVALIDO')
      );
      return;
    }

    if (
      !this.whatsappConfigDataService.$sessionName() ||
      !this.whatsappConfigDataService.$whatsappConfig()?.webhookUrl
    ) {
      this.notificacao.notificacaoWarn(
        this.translate.instant('BARDO_WHATSAPP.CONFIGURACOES.SESSAO_INCOMPLETA')
      );

      return;
    }

    this._salvando.set(true);

    try {
      const payload: ISession = {
        sessionName: this.whatsappConfigDataService.$sessionName(),
        webhookUrl:
          this.whatsappConfigDataService.$whatsappConfig()?.webhookUrl ?? '',
        settings: {
          ...this.settingsForm.value,
          // Mantém webhookEvents existentes (não editável pelo usuário)
          webhookEvents: this.whatsappConfigDataService.$whatsappConfig()
            ?.settings?.webhookEvents ?? [
            HandleEvent.HandleAck,
            HandleEvent.HandleStateChange,
            HandleEvent.HandleMessage,
          ],
        },
      };

      await this.whatsappService.atualizarConfiguracoesComportamento(payload);

      this.notificacao.notificacaoSuccess(
        this.translate.instant('BARDO_WHATSAPP.CONFIGURACOES.SUCESSO_SALVAR')
      );
    } catch (error) {
      console.error('Erro ao atualizar configurações:', error);
      this.notificacao.notificacaoError(
        this.translate.instant('BARDO_WHATSAPP.CONFIGURACOES.ERRO_SALVAR')
      );
    } finally {
      this._salvando.set(false);
    }
  }

  restaurarPadroes(): void {
    this.settingsForm.patchValue({
      rejectCalls: true,
      alwaysOnline: true,
      ignoreGroups: true,
      ignoreBroadcasts: true,
      typingSimulation: true,
    });

    this.notificacao.notificacaoSuccess(
      this.translate.instant('BARDO_WHATSAPP.CONFIGURACOES.PADROES_RESTAURADOS')
    );
  }
}
